import { TaskStatus } from '../types/domain';
import { ActivityStatus, CompleteResultKind, TaskButtonState, TaskCompleteCannotCode, TaskCompleteStatus } from '../types/shared';
// R007：后端 status → 按钮态映射
const BUTTON_STATE_BY_STATUS = {
    [TaskStatus.UNFINISHED]: TaskButtonState.UNFINISHED,
    [TaskStatus.FINISHED]: TaskButtonState.FINISHED,
    [TaskStatus.IMPOSSIBLE]: TaskButtonState.IMPOSSIBLE
};
/**
 * R007：任务状态判定 —— 按后端 status 映射按钮态。
 * （标题/进度已统一为「固定展示」，不再由运营配置或计数控制是否展进度。）
 */
export function deriveTaskStatus(task) {
    return {
        buttonState: BUTTON_STATE_BY_STATUS[task.status]
    };
}
// R007：按钮态 → 运营三态配置取值
function pickButtonConfig(cfg, buttonState) {
    switch (buttonState) {
        case TaskButtonState.FINISHED:
            return {
                image: cfg.finishedBtnImage,
                text: cfg.finishedBtnText
            };
        case TaskButtonState.IMPOSSIBLE:
            return {
                image: cfg.impossibleBtnImage,
                text: cfg.impossibleBtnText
            };
        default:
            return {
                image: cfg.unfinishedBtnImage,
                text: cfg.unfinishedBtnText
            };
    }
}
/**
 * R007：后端任务列表 + 运营配置 → 任务项 VO 列表。
 * 排序：以运营在组件维度配置的任务顺序（taskCenterConfig.tasks）为准；
 *       但「已完成 / 不可完成」状态的任务统一置底（各自仍保持配置内的相对顺序）。
 * 按 taskId 匹配运营配置，缺配置任务过滤；标题/描述用后端下发文案（已拍板）。
 */
export function resolveTaskListVo(rawQueryInfo, taskCenterConfig) {
    const taskConfigs = taskCenterConfig?.tasks ?? [];
    if (!taskConfigs.length)
        return [];
    const taskInfos = rawQueryInfo?.moduleInfoList?.find(m => m.taskListModuleInfo)?.taskListModuleInfo?.taskInfos ?? [];
    const taskMap = new Map(taskInfos.map(t => [t.taskId, t]));
    // 以运营配置顺序遍历，匹配后端任务构造 VO（后端没下发该任务则跳过）
    const vos = [];
    taskConfigs.forEach(cfg => {
        const task = taskMap.get(cfg.taskId);
        if (!task)
            return; // 后端未下发 → 不渲染
        const { buttonState } = deriveTaskStatus(task);
        const button = pickButtonConfig(cfg, buttonState);
        const extra = (task.extra ?? {});
        vos.push({
            taskId: task.taskId,
            taskType: task.taskType,
            bizLine: extra.commonExtra?.biz,
            iconImage: cfg.iconImage,
            title: task.taskTitle,
            // 固定展示后端标题
            buttonState,
            buttonImage: button.image,
            buttonText: button.text,
            // 固定展示完成进度（不再展示后端描述，也不再受运营开关控制）
            progressText: `今日可完成 ${task.dayCompletedCount ?? 0}/${task.dayCompletableCount ?? 0} 次`,
            raw: task
        });
    });
    // 已完成 / 不可完成 置底（稳定：各组内保持配置顺序）
    const isBottom = (vo) => vo.buttonState === TaskButtonState.FINISHED || vo.buttonState === TaskButtonState.IMPOSSIBLE;
    return [...vos.filter(vo => !isBottom(vo)), ...vos.filter(isBottom)];
}
/** R007：任务完成接口（3.8）响应分流：成功 / 风控 / 主客态相同 / 其他错误 */
export function parseCompleteResult(res) {
    if (res?.status === TaskCompleteStatus.SUCCESS) {
        return {
            ok: true,
            kind: CompleteResultKind.OK,
            rewards: res?.data?.leaderRewards ?? null
        };
    }
    if (res?.status === TaskCompleteStatus.USER_RISK) {
        return {
            ok: false,
            kind: CompleteResultKind.RISK,
            rewards: null
        };
    }
    if (res?.data?.cannotCode === TaskCompleteCannotCode.SAME_USER_ID_NOT_ALLOWED) {
        return {
            ok: false,
            kind: CompleteResultKind.SAME_USER,
            rewards: null
        };
    }
    return {
        ok: false,
        kind: CompleteResultKind.ERROR,
        rewards: null
    };
}
/** R007：推荐供给（3.18）响应取全量 poiId（过滤空值），空响应返回 []。
 *  点击「去浏览」按游标顺序轮取（见 pickPoiIdAt），取完一轮再从头开始。 */
export function pickRecPoiIds(recResp) {
    return (recResp?.remainRecPoiInfo ?? []).map(it => it?.poiId).filter((id) => id != null);
}
/** R007：按游标从 poiId 列表轮转取值 —— 取模实现「取完一轮回到第一个」；空列表返回 undefined。 */
export function pickPoiIdAt(list, cursor) {
    if (!list.length)
        return undefined;
    return list[(cursor % list.length + list.length) % list.length];
}
/** R007：奖励大弹窗命中判定（待检查的弹窗 key 列表由调用方传入，避免依赖 hook 层枚举） */
export function hasRewardPopup(popupMap, rewardPopupKeys) {
    return rewardPopupKeys.some(key => !!popupMap?.[key]);
}
/** R007：任务入口可见性：仅参与中态展示，主/客态均展示 */
export function shouldShowEntry(activityStatus, _isGuest) {
    return activityStatus === ActivityStatus.ON_PROGRESS;
}
/**
 * R007：init 刷新弹窗图的 merge 策略 —— 整图替换会把打开着的任务半页弹窗关掉，
 * 改为保留 preserveKeys（如 TASK_LIST_MODAL）的当前值，其余 key 按新图。
 */
export function mergePopupMapPreserving(nextPopupMap, prevPopupMap, preserveKeys) {
    if (!prevPopupMap)
        return nextPopupMap;
    const merged = {
        ...nextPopupMap
    };
    preserveKeys.forEach(key => {
        if (key in prevPopupMap)
            merged[key] = prevPopupMap[key];
    });
    return merged;
}
